import { LESSONS } from '../content/lessons';
import { PRESETS } from '../content/presets';
import type { Block, Lesson, Question } from '../content/types';
import { metricText, summarizeRun } from '../simulation/metrics';
import { buildContext, evidenceRuns, type Answer, type ProgressState } from './progress';
import { describeController, expandTrace, FF_LABELS, type RunRecord } from './runRecord';

function stripMarkup(text: string): string {
  return text
    .replace(/\{\{([^}|]+)(?:\|((?:(?!\}\}).)+))?\}\}/g, (_, id: string, shown?: string) => shown ?? id)
    .replace(/\$([^$]+)\$/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}

function runMetrics(r: RunRecord) {
  return summarizeRun(expandTrace(r.trace), { tolerance: 0.05, finalWindow: 2 });
}

/** Collect predict/reflect blocks from a lesson's sections. */
function responseBlocks(lesson: Lesson): { predicts: Extract<Block, { type: 'predict' }>[]; reflects: Extract<Block, { type: 'reflect' }>[] } {
  const predicts: Extract<Block, { type: 'predict' }>[] = [];
  const reflects: Extract<Block, { type: 'reflect' }>[] = [];
  const walk = (blocks: Block[]) => {
    for (const b of blocks) {
      if (b.type === 'predict') predicts.push(b);
      else if (b.type === 'reflect') reflects.push(b);
      else if (b.type === 'optional') walk(b.blocks);
    }
  };
  for (const s of lesson.sections) walk(s.blocks);
  return { predicts, reflects };
}

function formatAnswer(q: Question, answer: Answer): string {
  if (answer === null || answer === undefined || answer === '') return '(no answer)';
  switch (q.kind) {
    case 'numeric':
      return `${answer} ${q.unit}`;
    case 'choice': {
      const opt = q.options.find((o) => o.id === answer);
      return opt ? stripMarkup(opt.text) : String(answer);
    }
    case 'match': {
      const a = (typeof answer === 'object' && !Array.isArray(answer) ? answer : {}) as Record<string, string>;
      return q.items
        .map((it) => {
          const choice = q.choices.find((c) => c.id === a[it.id]);
          return `${stripMarkup(it.text)} → ${choice ? stripMarkup(choice.text) : '(blank)'}`;
        })
        .join('; ');
    }
    case 'order': {
      const order = Array.isArray(answer) ? answer : [];
      return order
        .map((id, i) => {
          const it = q.items.find((x) => x.id === id);
          return `${i + 1}. ${it ? stripMarkup(it.text) : id}`;
        })
        .join(' ');
    }
  }
}

function formatSchedule(label: string, initial: number, steps: { t: number; value: number }[], unit: string): string {
  const parts = [`${initial} ${unit}`];
  for (const s of steps) parts.push(`${s.value} ${unit} at ${s.t} s`);
  return `${label}: ${parts.join(', then ')}`;
}

function formatRun(r: RunRecord): string[] {
  const preset = PRESETS[r.presetId];
  const m = runMetrics(r);
  const c = r.config.controller;
  const lines = [
    `Run ${r.number} · ${preset?.title ?? r.presetId} · ${r.status}`,
    `  Controller: ${describeController(c)} · ${FF_LABELS[c.ffMode]}`,
    `  ${formatSchedule('Target', r.config.reference.initial, r.config.reference.steps, 'm')}`,
    `  ${formatSchedule('Disturbance', r.config.disturbance.initial, r.config.disturbance.steps, 'N')}`,
    `  Plant: m=${r.config.plant.m} kg, k=${r.config.plant.k} N/m, b=${r.config.plant.b} N·s/m, T_max=${r.config.plant.Tmax} N`,
    `  Duration ${r.config.duration} s · Noise ${r.config.noise.enabled ? `on (σ=${r.config.noise.sigma} m)` : 'off'}`,
    `  Metrics: rise ${metricText(m.riseTime, 's')}; overshoot ${
      m.overshoot.status === 'ok' ? `${(m.overshoot.value! * 100).toFixed(1)}%` : metricText(m.overshoot, '')
    }; settling ${metricText(m.settlingTime, 's')}; final 2 s mean error ${metricText(m.finalMeanError, 'm', 4)}`,
    `  Stats: altitude ${r.stats.minAltitude.toFixed(3)}–${r.stats.maxAltitude.toFixed(3)} m; saturation ${r.stats.saturationTime.toFixed(2)} s; IAE ${r.stats.iae.toFixed(3)} m·s${
      c.kind === 'bang-bang' ? `; switches ${r.stats.switchCount}` : ''
    }`,
  ];
  if (r.stopReason) lines.push(`  Stopped: ${r.stopReason.message}`);
  return lines;
}

export interface WorkExportInput {
  progress: ProgressState;
  runs: RunRecord[];
  predictions: Record<string, string>;
  reflections: Record<string, string>;
  studentId: string;
}

/** Build plain-text report lines for the student turn-in PDF. */
export function buildWorkReport(input: WorkExportInput): string[] {
  const { progress, runs, predictions, reflections, studentId } = input;
  const byId = new Map(runs.map((r) => [r.id, r]));
  const ctx = buildContext(evidenceRuns(progress, runs));
  const id = studentId.trim() || 'Student Work';
  const lines: string[] = [
    `Altitude Control Lab — ${id}`,
    `Exported ${new Date().toLocaleString()}`,
    '',
  ];

  for (const lesson of LESSONS) {
    const gate = progress.gates[lesson.gate.id];
    const { predicts, reflects } = responseBlocks(lesson);
    lines.push(`Module ${lesson.number}: ${lesson.title}`);
    lines.push(gate?.passed ? `Check: passed${gate.summary ? ` — ${gate.summary}` : ''}` : gate ? `Check: not yet passed (${gate.attempts} attempt(s))` : 'Check: not started');
    lines.push('');

    if (predicts.length) {
      lines.push('Predictions');
      for (const p of predicts) {
        const choice = predictions[p.id];
        const opt = p.options.find((o) => o.id === choice);
        lines.push(`  Q: ${stripMarkup(p.prompt)}`);
        lines.push(`  A: ${opt ? stripMarkup(opt.text) : choice ? choice : '(not answered)'}`);
      }
      lines.push('');
    }

    if (reflects.length) {
      lines.push('Reflections');
      for (const r of reflects) {
        const text = reflections[r.id]?.trim();
        lines.push(`  Prompt: ${stripMarkup(r.prompt)}`);
        lines.push(`  Response: ${text || '(blank)'}`);
      }
      lines.push('');
    }

    lines.push('Check answers');
    for (const q of lesson.gate.questions) {
      const item = gate?.items[q.id];
      let prompt: string;
      try {
        prompt = stripMarkup(q.prompt(ctx));
      } catch {
        prompt = `(question ${q.id} — needs lab evidence to show the full prompt)`;
      }
      lines.push(`  Q: ${prompt}`);
      lines.push(`  A: ${item ? formatAnswer(q, item.answer) : '(no answer)'}${item ? (item.correct ? ' [correct]' : ' [not marked correct]') : ''}`);
      const runIds = item?.runIds ?? {};
      const boundNums = q.binds
        .map((stepId) => {
          const runId = runIds[stepId] ?? progress.evidence[stepId] ?? gate?.evidence?.[stepId];
          return runId ? byId.get(runId)?.number : undefined;
        })
        .filter((n): n is number => n !== undefined);
      if (boundNums.length) lines.push(`  Uses saved run(s): ${boundNums.map((n) => `Run ${n}`).join(', ')}`);
    }
    lines.push('');

    // Lab evidence runs for this module.
    const evidenceOnly = lesson.lab.steps
      .map((s) => {
        const id = gate?.evidence?.[s.id] ?? progress.evidence[s.id];
        const run = id ? byId.get(id) : undefined;
        return run ? { step: s, run } : null;
      })
      .filter((x): x is { step: (typeof lesson.lab.steps)[number]; run: RunRecord } => !!x);
    if (evidenceOnly.length) {
      lines.push('Lab runs (saved evidence)');
      for (const { step, run } of evidenceOnly) {
        lines.push(`  Step: ${stripMarkup(step.title)}`);
        for (const row of formatRun(run)) lines.push(`  ${row}`);
      }
      lines.push('');
    }
  }

  const used = new Set<string>();
  for (const lesson of LESSONS) {
    const gate = progress.gates[lesson.gate.id];
    for (const s of lesson.lab.steps) {
      const id = gate?.evidence?.[s.id] ?? progress.evidence[s.id];
      if (id) used.add(id);
    }
    if (gate) for (const item of Object.values(gate.items)) for (const id of Object.values(item.runIds)) used.add(id);
  }
  const extras = runs.filter((r) => !used.has(r.id));
  if (extras.length) {
    lines.push('Other saved runs');
    for (const r of extras) for (const row of formatRun(r)) lines.push(row);
    lines.push('');
  }

  return lines;
}

/** Escape for PDF literal strings (WinAnsi subset). */
function pdfEscape(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

/** Map common Unicode to WinAnsi-friendly ASCII for built-in Helvetica. */
function toPdfText(s: string): string {
  return s
    .replace(/\u2022/g, '-')
    .replace(/[–—]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/×/g, 'x')
    .replace(/·/g, '.')
    .replace(/±/g, '+/-')
    .replace(/≤/g, '<=')
    .replace(/≥/g, '>=')
    .replace(/σ/g, 'sigma')
    .replace(/τ/g, 'tau')
    .replace(/ζ/g, 'zeta')
    .replace(/ω/g, 'omega')
    .replace(/Δ/g, 'Delta')
    .replace(/→/g, '->')
    .replace(/✓/g, '[ok]')
    .replace(/✗/g, '[x]')
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '?');
}

function wrapLine(text: string, maxChars: number): string[] {
  if (text.length <= maxChars) return [text];
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const out: string[] = [];
  let cur = '';
  const pushChunk = (chunk: string) => {
    while (chunk.length > maxChars) {
      out.push(chunk.slice(0, maxChars));
      chunk = chunk.slice(maxChars);
    }
    cur = chunk;
  };
  for (const w of words) {
    if (!cur) {
      pushChunk(w);
      continue;
    }
    if (cur.length + 1 + w.length <= maxChars) cur += ` ${w}`;
    else {
      out.push(cur);
      pushChunk(w);
    }
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
}

/** Build a simple multi-page PDF (Helvetica) from report lines. */
export function linesToPdf(rawLines: string[]): Uint8Array {
  const fontSize = 10;
  const lineHeight = 12;
  const margin = 54; // 0.75 in
  const pageWidth = 612; // letter
  const pageHeight = 792;
  const maxWidth = pageWidth - margin * 2;
  const maxChars = Math.floor(maxWidth / (fontSize * 0.5));
  const top = pageHeight - margin;
  const bottom = margin;

  const wrapped: string[] = [];
  for (const line of rawLines) {
    const plain = toPdfText(line);
    if (!plain) {
      wrapped.push('');
      continue;
    }
    wrapped.push(...wrapLine(plain, maxChars));
  }

  const linesPerPage = Math.max(1, Math.floor((top - bottom) / lineHeight));
  const pages: string[][] = [];
  for (let i = 0; i < wrapped.length; i += linesPerPage) pages.push(wrapped.slice(i, i + linesPerPage));
  if (!pages.length) pages.push(['(empty)']);

  const objects: string[] = [];
  const add = (body: string) => {
    objects.push(body);
    return objects.length;
  };

  const kids: number[] = [];
  const contentIds: number[] = [];
  for (const pageLines of pages) {
    const ops: string[] = ['BT', `/F1 ${fontSize} Tf`, `${margin} ${top} Td`, `${lineHeight} TL`];
    pageLines.forEach((line, i) => {
      if (i > 0) ops.push('T*');
      ops.push(`(${pdfEscape(line)}) Tj`);
    });
    ops.push('ET');
    const stream = ops.join('\n');
    contentIds.push(add(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`));
  }

  const fontId = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  for (let i = 0; i < pages.length; i++) {
    kids.push(
      add(
        `<< /Type /Page /Parent 0 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents ${contentIds[i]} 0 R /Resources << /Font << /F1 ${fontId} 0 R >> >> >>`,
      ),
    );
  }
  const pagesId = add(`<< /Type /Pages /Kids [${kids.map((id) => `${id} 0 R`).join(' ')}] /Count ${kids.length} >>`);
  // Patch Parent refs now that pagesId is known.
  for (const id of kids) {
    objects[id - 1] = objects[id - 1].replace('/Parent 0 0 R', `/Parent ${pagesId} 0 R`);
  }
  const catalogId = add(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [0];
  for (let i = 0; i < objects.length; i++) {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${objects[i]}\nendobj\n`;
  }
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += '0000000000 65535 f \n';
  for (let i = 1; i <= objects.length; i++) {
    pdf += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new TextEncoder().encode(pdf);
}

function filenameSafeId(id: string): string {
  const cleaned = id
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return cleaned || 'student';
}

export function downloadWorkPdf(input: WorkExportInput): void {
  const bytes = linesToPdf(buildWorkReport(input));
  const blob = new Blob([bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer], {
    type: 'application/pdf',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `altitude-control-work-${filenameSafeId(input.studentId)}-${new Date().toISOString().slice(0, 10)}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
